import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  DollarSign, AlertCircle, CheckCircle2, ChevronRight, 
  Users, Mail, TrendingDown, CreditCard, Send
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

import API from '@/config/api';
// API imported from config;

const PaymentStatusWidget = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [worksheet, setWorksheet] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    fetchWorksheet();
  }, []);

  const fetchWorksheet = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const nextWeek = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const response = await axios.get(
        `${API}/billing/worksheet?start_date=${today}&end_date=${nextWeek}`,
        getAuthHeaders()
      );
      setWorksheet(response.data.worksheet || []);
    } catch (error) {
      console.error('Failed to fetch billing worksheet:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats
  const unpaidTuition = worksheet.filter(e => !e.tuition_paid && e.tuition_amount > 0);
  const unpaidRegistration = worksheet.filter(e => !e.registration_paid && e.registration_fee > 0);
  
  const totalUnpaidTuition = unpaidTuition.reduce((sum, e) => 
    sum + Math.max(0, (e.tuition_amount || 0) - (e.tuition_state_payment || 0)), 0);
  const totalUnpaidReg = unpaidRegistration.reduce((sum, e) => 
    sum + Math.max(0, (e.registration_fee || 0) - (e.registration_state_payment || 0)), 0);
  const totalUnpaid = totalUnpaidTuition + totalUnpaidReg;

  // Get unique parents with unpaid balances
  const parentsWithUnpaid = new Set([
    ...unpaidTuition.map(e => e.parent_id),
    ...unpaidRegistration.map(e => e.parent_id)
  ].filter(Boolean));

  const stats = {
    totalOutstanding: totalUnpaid,
    unpaidCount: unpaidTuition.length + unpaidRegistration.length,
    parentsOwing: parentsWithUnpaid.size,
    collectionRate: worksheet.length > 0 
      ? Math.round(((worksheet.length - unpaidTuition.length) / worksheet.length) * 100)
      : 100
  };

  // Combine unpaid items and sort by amount (highest first)
  const allUnpaid = [
    ...unpaidTuition.map(e => ({
      ...e,
      type: 'tuition',
      amountDue: Math.max(0, (e.tuition_amount || 0) - (e.tuition_state_payment || 0))
    })),
    ...unpaidRegistration.map(e => ({
      ...e,
      type: 'registration',
      amountDue: Math.max(0, (e.registration_fee || 0) - (e.registration_state_payment || 0))
    }))
  ].sort((a, b) => b.amountDue - a.amountDue);

  const handleSendReminders = async () => {
    if (allUnpaid.length === 0) {
      toast.info('No unpaid balances to remind');
      return;
    }

    setSending(true);
    try {
      const unpaidItems = allUnpaid.map(e => ({
        child_id: e.child_id,
        child_name: e.child_name,
        parent_id: e.parent_id,
        type: e.type,
        amount: e.amountDue
      })).filter(item => item.parent_id);

      const response = await axios.post(
        `${API}/billing/send-payment-reminders`,
        { unpaid_items: unpaidItems },
        getAuthHeaders()
      );
      
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to send reminders');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Card className="card-clay">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-1/3"></div>
            <div className="h-8 bg-muted rounded w-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasUnpaid = stats.unpaidCount > 0;

  return (
    <Card className="card-clay" data-testid="payment-status-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-primary" />
            Payment Status
            {hasUnpaid && (
              <Badge variant="destructive" className="ml-2">
                {stats.unpaidCount} unpaid
              </Badge>
            )}
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-muted-foreground"
          >
            {isExpanded ? 'Collapse' : 'Expand'}
          </Button>
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          <div className={`p-3 rounded-lg text-center border ${
            stats.totalOutstanding > 0 
              ? 'bg-red-50 border-red-200' 
              : 'bg-green-50 border-green-200'
          }`}>
            <div className={`text-2xl font-bold ${
              stats.totalOutstanding > 0 ? 'text-red-600' : 'text-green-600'
            }`}>
              ${stats.totalOutstanding.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </div>
            <div className="text-xs text-muted-foreground">Outstanding</div>
          </div>
          <div className="p-3 bg-amber-50 rounded-lg text-center border border-amber-200">
            <div className="text-2xl font-bold text-amber-600">{stats.unpaidCount}</div>
            <div className="text-xs text-amber-600">Unpaid Items</div>
          </div>
          <div className="p-3 bg-blue-50 rounded-lg text-center border border-blue-200">
            <div className="text-2xl font-bold text-blue-600">{stats.parentsOwing}</div>
            <div className="text-xs text-blue-600">Parents Owing</div>
          </div>
          <div className="p-3 bg-green-50 rounded-lg text-center border border-green-200">
            <div className="text-2xl font-bold text-green-600">{stats.collectionRate}%</div>
            <div className="text-xs text-green-600">Collection Rate</div>
          </div>
        </div>

        {/* Collection Progress */}
        <div className="mb-4 p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Tuition Collection</span>
            <span className="text-sm font-bold">{stats.collectionRate}%</span>
          </div>
          <Progress 
            value={stats.collectionRate} 
            className={`h-2 ${
              stats.collectionRate >= 90 
                ? '[&>div]:bg-green-500' 
                : stats.collectionRate >= 70 
                  ? '[&>div]:bg-amber-500'
                  : '[&>div]:bg-red-500'
            }`}
          />
        </div>

        {isExpanded && (
          <>
            {/* Quick Actions */}
            {hasUnpaid && (
              <div className="flex gap-2 mb-4">
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleSendReminders}
                  disabled={sending}
                  className="flex-1"
                  data-testid="widget-send-reminders"
                >
                  {sending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Send All Reminders
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate('/billing')}
                  data-testid="widget-view-billing"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  View Billing
                </Button>
              </div>
            )}

            {/* Unpaid Items List */}
            {hasUnpaid ? (
              <ScrollArea className="h-[220px]">
                <div className="space-y-2">
                  {allUnpaid.slice(0, 10).map((item, idx) => (
                    <div
                      key={`${item.child_id}-${item.type}-${idx}`}
                      className="p-3 rounded-lg bg-red-50 border border-red-200 hover:border-red-300 cursor-pointer transition-all"
                      onClick={() => navigate('/billing')}
                      data-testid={`unpaid-item-${idx}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-red-200 flex items-center justify-center text-red-700 text-sm font-bold">
                            {item.child_name?.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div>
                            <div className="font-medium text-sm">{item.child_name}</div>
                            <div className="text-xs text-muted-foreground">
                              <Badge variant="outline" className="text-[10px] py-0 px-1.5 mr-1">
                                {item.type === 'tuition' ? 'Tuition' : 'Registration'}
                              </Badge>
                              {item.state_assisted && (
                                <Badge variant="outline" className="text-[10px] py-0 px-1.5 text-green-600 border-green-300">
                                  State Assisted
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right">
                            <div className="text-sm font-bold text-red-600">
                              ${item.amountDue.toFixed(2)}
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {allUnpaid.length > 10 && (
                    <div className="text-center text-sm text-muted-foreground py-2">
                      +{allUnpaid.length - 10} more unpaid items
                    </div>
                  )}
                </div>
              </ScrollArea>
            ) : (
              <div className="text-center py-8">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-green-500" />
                <p className="text-green-600 font-medium">All payments collected!</p>
                <p className="text-sm text-muted-foreground mt-1">Great job staying on top of billing</p>
              </div>
            )}

            {/* View All Button */}
            <div className="mt-4 pt-4 border-t">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => navigate('/billing')}
              >
                <DollarSign className="w-4 h-4 mr-2" />
                View Full Billing Worksheet
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PaymentStatusWidget;
